import { useState } from "react";
import { Button, Card, Grid, Group, SegmentedControl, Skeleton, Stack, Text } from "@mantine/core";
import { IconSparkles } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import type { IngredientExtractOutput } from "@passio/api-client";
import { passio } from "../lib/clients";
import { PageHeader } from "../theme/components";
import { EmptyState, ErrorNotice, RequireFeature, ResultHeader } from "../components/common";
import { FoodDetailDrawer, FoodDetailPanel, MealGroupsView } from "../components/food";
import { ImagePicker } from "../components/ImagePicker";
import { FeedbackButtons } from "../components/FeedbackButtons";
import { RouteModelPicker } from "../components/ModelPicker";
import { SAMPLE_PHOTOS } from "../lib/samples";
import { composeMessage } from "../lib/clientSettings";
import { AiContextFields } from "../components/AiContextFields";

type Mode = "meal" | "plate" | "packaged";
type VisionReq = { image: string; message?: string };

const MODE_NOTES: Record<Mode, string> = {
  meal: "extractIngredientsAutoTyped works out what kind of photo it is (plate, packaged food, barcode, label) and picks the right pipeline. Use this for “point the camera at anything”.",
  plate: "vision/extractIngredients forces the cooked/raw food pipeline: no packaged-product or barcode handling. Use it when you already know it's a plate.",
  packaged: "vision/extractPackagedProduct reads the package or nutrition panel into a full food with label nutrition.",
};

export function PhotoPage() {
  const [mode, setMode] = useState<Mode>("meal");
  const [image, setImage] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [selected, setSelected] = useState<IngredientExtractOutput | null>(null);

  const meal = useMutation({ mutationFn: (req: VisionReq) => passio.vision.extractIngredientsAutoTyped(req) });
  const plate = useMutation({ mutationFn: (req: VisionReq) => passio.vision.extractIngredients(req) });
  const packaged = useMutation({ mutationFn: (req: VisionReq) => passio.vision.extractPackagedProduct(req) });
  const active = { meal, plate, packaged }[mode];

  const analyze = () => {
    if (!image) return;
    active.mutate({ image, message: composeMessage(message) });
  };

  return (
    <RequireFeature feature="vision">
      <PageHeader
        eyebrow="POST /napi/tools/vision/extractIngredientsAutoTyped · /extractIngredients · /extractPackagedProduct"
        title="Photo recognition"
        description="Meal photos come back as typed foods grouped into meals and components, each matched to the database with portions. Package photos become a full food with label nutrition."
        actions={<RouteModelPicker routes={["extractIngredientsAutoTyped", "visionExtractIngredients", "extractPackagedProduct"]} />}
      />
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Card>
            <Stack gap="md">
              <SegmentedControl
                fullWidth
                value={mode}
                onChange={(v) => setMode(v as Mode)}
                data={[
                  { value: "meal", label: "Any food" },
                  { value: "plate", label: "Plate" },
                  { value: "packaged", label: "Package" },
                ]}
              />
              <Text fz="xs" c="dimmed">
                {MODE_NOTES[mode]}
              </Text>
              <ImagePicker
                value={image}
                onChange={setImage}
                label={mode === "packaged" ? "Drop a photo of the package or nutrition label" : "Drop a photo of a meal or plate"}
                samples={mode === "packaged" ? undefined : SAMPLE_PHOTOS}
              />
              <AiContextFields note={message} onNoteChange={setMessage} placeholder={mode === "packaged" ? "e.g. the 500 ml bottle" : "e.g. homemade, large portion, no cheese"} />
              <Button leftSection={<IconSparkles size={16} />} onClick={analyze} disabled={!image} loading={active.isPending} size="md">
                Analyze photo
              </Button>
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>
          {active.error && <ErrorNotice error={active.error} onRetry={analyze} />}
          {active.isPending && (
            <Card>
              <Text fw={650} mb="sm">
                Identifying foods…
              </Text>
              <Stack gap="xs">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} h={56} radius="md" />
                ))}
              </Stack>
            </Card>
          )}
          {mode === "meal" && meal.data && !meal.isPending && (
            <Stack gap="sm">
              <Group justify="space-between">
                <ResultHeader title="What we found" count={meal.data.data.length} meta={meal.data.meta} feedback={<FeedbackButtons requestId={meal.data.meta.requestId} />} />
              </Group>
              <MealGroupsView items={meal.data.data} onSelect={setSelected} fallbackMealName="Your photo" emptyText="No foods spotted. Try a brighter photo taken from above with the whole plate in frame." />
            </Stack>
          )}
          {mode === "plate" && plate.data && !plate.isPending && (
            <Stack gap="sm">
              <ResultHeader title="Foods on the plate" count={plate.data.data.length} meta={plate.data.meta} feedback={<FeedbackButtons requestId={plate.data.meta.requestId} />} />
              <MealGroupsView items={plate.data.data} onSelect={setSelected} fallbackMealName="Your plate" emptyText="No foods spotted. Try a brighter photo taken from above." />
            </Stack>
          )}
          {mode === "packaged" && packaged.data && !packaged.isPending && (
            <Card>
              <ResultHeader title="Product from photo" meta={packaged.data.meta} feedback={<FeedbackButtons requestId={packaged.data.meta.requestId} />} />
              <FoodDetailPanel key={packaged.data.record.id} data={packaged.data.data} />
            </Card>
          )}
          {!active.data && !active.isPending && !active.error && (
            <EmptyState title="Add a photo to analyze">{mode === "packaged" ? "Front of pack or the nutrition facts panel both work." : "Mixed plates work best: the response keeps meal and component names so dishes stay grouped."}</EmptyState>
          )}
        </Grid.Col>
      </Grid>
      <FoodDetailDrawer refCode={selected?.refCode} name={selected?.displayName} amount={selected} onClose={() => setSelected(null)} />
    </RequireFeature>
  );
}
